import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Truck,
  MapPin,
  Phone,
  Clock,
  UserPlus,
  Send,
  CheckCircle2,
  Printer,
  Search,
  Loader2,
  RefreshCw,
  Banknote,
  ClipboardCheck,
} from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
  Pill,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { aed } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const STATUS_TONES = {
  Preparing: { tone: "amber", bar: "border-l-amber-500" },
  Dispatched: { tone: "blue", bar: "border-l-blue-500" },
  Delivered: { tone: "green", bar: "border-l-emerald-500" },
  Cancelled: { tone: "red", bar: "border-l-red-500" },
};

const FILTER_TABS = [
  { id: "all", label: "All" },
  { id: "preparing", label: "Preparing" },
  { id: "dispatched", label: "Dispatched" },
  { id: "delivered", label: "Delivered" },
];

export default function DeliveryManagement() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const branchId = user?.default_branch?.id;

  const [view, setView] = useState("orders");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    const bill = location.state?.searchBill;
    if (!bill) return;
    setSearch(bill);
    setView("orders");
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);
  const [assignOrderId, setAssignOrderId] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState("");

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["delivery-orders", branchId, filter, search],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery/orders", {
        params: {
          branch_id: branchId,
          status: filter,
          search: search || undefined,
        },
      });
      return res;
    },
    enabled: Boolean(branchId),
    refetchInterval: 30000,
  });

  const { data: driversData } = useQuery({
    queryKey: ["delivery-persons-available", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery-persons/available", {
        params: { branch_id: branchId },
      });
      return res;
    },
    enabled: Boolean(branchId) && view === "orders",
    refetchInterval: 30000,
  });

  const { data: settlementData, isLoading: settlementLoading, refetch: refetchSettlement } = useQuery({
    queryKey: ["delivery-settlements", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/delivery/settlements/summary", {
        params: { branch_id: branchId },
      });
      return res;
    },
    enabled: Boolean(branchId),
    refetchInterval: 30000,
  });

  const orders = data?.orders ?? [];
  const counts = data?.counts ?? { all: 0, preparing: 0, dispatched: 0, delivered: 0 };
  const drivers = driversData?.delivery_persons ?? [];

  const statusMutation = useMutation({
    mutationFn: async ({ orderId, advance, delivery_status }) => {
      const { data: res } = await api.patch(`/delivery/orders/${orderId}/status`, {
        advance: advance || undefined,
        delivery_status: delivery_status || undefined,
      }, { params: { branch_id: branchId } });
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Status updated");
      queryClient.invalidateQueries({ queryKey: ["delivery-orders", branchId] });
      queryClient.invalidateQueries({ queryKey: ["delivery-persons-available", branchId] });
    },
    onError: (err) => {
      const msg =
        err.response?.data?.errors?.delivery_status?.[0]
        || err.response?.data?.errors?.delivery_person_id?.[0]
        || err.response?.data?.message
        || "Failed to update status";
      toast.error(msg);
    },
  });

  const assignMutation = useMutation({
    mutationFn: async ({ orderId, deliveryPersonId }) => {
      const { data: res } = await api.post(`/delivery/orders/${orderId}/assign-driver`, {
        delivery_person_id: deliveryPersonId || undefined,
      }, { params: { branch_id: branchId } });
      return res;
    },
    onSuccess: (res) => {
      toast.success(`Driver assigned: ${res.order?.driver || "OK"}`);
      setAssignOrderId(null);
      setSelectedDriverId("");
      queryClient.invalidateQueries({ queryKey: ["delivery-orders", branchId] });
      queryClient.invalidateQueries({ queryKey: ["delivery-persons-available", branchId] });
    },
    onError: (err) => {
      toast.error(
        err.response?.data?.errors?.delivery_person_id?.[0]
        || err.response?.data?.message
        || "Assignment failed",
      );
    },
  });

  const settleMutation = useMutation({
    mutationFn: async ({ orderId, deliveryPersonId, billIds }) => {
      if (orderId) {
        const { data: res } = await api.post(
          `/delivery/orders/${orderId}/settle`,
          {},
          { params: { branch_id: branchId } },
        );
        return res;
      }
      const { data: res } = await api.post(
        "/delivery/settlements/settle-batch",
        {
          delivery_person_id: deliveryPersonId || undefined,
          bill_ids: billIds || undefined,
        },
        { params: { branch_id: branchId } },
      );
      return res;
    },
    onSuccess: (res) => {
      toast.success(res.message || "Settlement recorded");
      queryClient.invalidateQueries({ queryKey: ["delivery-orders", branchId] });
      queryClient.invalidateQueries({ queryKey: ["delivery-settlements", branchId] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || err.response?.data?.errors?.bill?.[0] || "Settlement failed");
    },
  });

  const printMutation = useMutation({
    mutationFn: async (orderId) => {
      await api.post(`/bills/${orderId}/reprint`, {}, { params: { branch_id: branchId } });
    },
    onSuccess: () => toast.success("Bill sent to printer"),
    onError: () => toast.error("Print failed"),
  });

  const tabCounts = useMemo(
    () => ({
      all: counts.all ?? 0,
      preparing: counts.preparing ?? 0,
      dispatched: counts.dispatched ?? 0,
      delivered: counts.delivered ?? 0,
    }),
    [counts],
  );

  return (
    <PageContainer className="!max-w-none">
      <PageHeader
        title="Delivery Management"
        subtitle="Assign drivers and track delivery status in real time"
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              className="h-9"
              data-testid="delivery-refresh"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? (
                <Loader2 size={14} className="mr-1.5 animate-spin" />
              ) : (
                <RefreshCw size={14} className="mr-1.5" />
              )}
              Refresh
            </Button>
            <Button
              size="sm"
              className="h-9 bg-[#2563EB] hover:bg-blue-700"
              data-testid="delivery-new"
              onClick={() => navigate("/pos")}
            >
              <Truck size={14} className="mr-1.5" /> New Delivery
            </Button>
          </>
        }
      />

      <Tabs value={view} onValueChange={setView} className="mb-4">
        <TabsList className="h-9 bg-slate-100">
          <TabsTrigger value="orders" className="text-[12.5px] h-7 px-4" data-testid="delivery-view-orders">
            <Truck size={13} className="mr-1.5" /> Active Orders
          </TabsTrigger>
          <TabsTrigger value="settlement" className="text-[12.5px] h-7 px-4" data-testid="delivery-view-settlement">
            <Banknote size={13} className="mr-1.5" /> Driver Settlement
            {(settlementData?.pending_count ?? 0) > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-mono border border-amber-300">
                {settlementData.pending_count}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {view === "settlement" ? (
        <div className="space-y-4">
          <Card className="p-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[13px] font-semibold text-slate-800">Pending driver settlement</div>
              <div className="text-[12px] text-slate-500 mt-0.5">
                Delivered orders awaiting cash / COD handover from drivers
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10.5px] uppercase tracking-wide text-slate-400">Total pending</div>
              <div className="text-[20px] font-bold text-slate-900">
                {aed(settlementData?.pending_amount ?? 0)}
              </div>
              <div className="text-[11px] text-slate-500">{settlementData?.pending_count ?? 0} orders</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetchSettlement()} disabled={settlementLoading}>
              <RefreshCw size={14} className={cn("mr-1.5", settlementLoading && "animate-spin")} /> Refresh
            </Button>
          </Card>

          {settlementLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
              <Loader2 size={18} className="animate-spin" /> Loading settlements…
            </div>
          ) : (settlementData?.drivers ?? []).length === 0 ? (
            <Card className="p-8 text-center text-slate-500 text-[13px]">
              No pending settlements. Delivered &amp; paid orders appear here until settled.
            </Card>
          ) : (
            (settlementData?.drivers ?? []).map((group) => (
              <Card key={group.delivery_person_id} className="overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between gap-3 bg-slate-50">
                  <div>
                    <div className="text-[14px] font-semibold text-slate-900">{group.driver_name}</div>
                    <div className="text-[11.5px] text-slate-500">
                      {group.order_count} order(s) · Cash {aed(group.cash_amount)} · Total {aed(group.total_amount)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="h-8 bg-emerald-700 hover:bg-emerald-800"
                    data-testid={`settle-driver-${group.delivery_person_id}`}
                    disabled={settleMutation.isPending}
                    onClick={() => settleMutation.mutate({ deliveryPersonId: group.delivery_person_id })}
                  >
                    <ClipboardCheck size={14} className="mr-1.5" /> Settle All
                  </Button>
                </div>
                <ul className="divide-y">
                  {group.orders.map((o) => (
                    <li key={o.id} className="px-4 py-2.5 flex items-center gap-3 text-[12.5px]">
                      <div className="font-mono font-bold text-blue-700 w-20">{o.bill_number}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-800 truncate">{o.customer_name}</div>
                        <div className="text-[11px] text-slate-500 truncate">{o.address}</div>
                      </div>
                      <Pill tone={o.payment_method === "cash" ? "amber" : "default"}>
                        {(o.payment_method || "paid").toUpperCase()}
                      </Pill>
                      <div className="font-bold text-slate-900 w-24 text-right">{aed(o.amount)}</div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-[11px]"
                        disabled={settleMutation.isPending}
                        onClick={() => settleMutation.mutate({ orderId: o.id })}
                      >
                        Settle
                      </Button>
                    </li>
                  ))}
                </ul>
              </Card>
            ))
          )}
        </div>
      ) : (
      <div className="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-4">
        <div>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <Tabs value={filter} onValueChange={setFilter}>
              <TabsList className="h-9 bg-slate-100">
                {FILTER_TABS.map((s) => (
                  <TabsTrigger
                    key={s.id}
                    value={s.id}
                    className="text-[12.5px] h-7 px-3"
                    data-testid={`delivery-filter-${s.label}`}
                  >
                    {s.label}
                    <span className="ml-1.5 px-1.5 py-0.5 rounded bg-white text-slate-600 text-[10px] font-mono border">
                      {tabCounts[s.id]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="w-64 flex items-center bg-white border border-[#E5E7EB] rounded-md h-9 px-2.5">
              <Search size={14} className="text-slate-400 mr-2" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by order, customer, phone…"
                className="border-0 shadow-none focus-visible:ring-0 h-7 px-0 bg-transparent text-[12.5px]"
                data-testid="delivery-search"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-slate-500 gap-2">
              <Loader2 size={18} className="animate-spin" /> Loading delivery orders…
            </div>
          ) : orders.length === 0 ? (
            <Card className="p-8 text-center text-slate-500 text-[13px]">
              No delivery orders in this view. Create one from POS (F7 Delivery).
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {orders.map((o) => {
                const t = STATUS_TONES[o.status] || STATUS_TONES.Preparing;
                const isAssigning = assignOrderId === o.id;
                return (
                  <div
                    key={o.id}
                    data-testid={`delivery-card-${o.bill_number}`}
                    className={cn(
                      "bg-white border border-[#E5E7EB] rounded-xl p-4 shadow-sm border-l-4",
                      t.bar,
                    )}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-mono font-bold text-blue-700 text-[14px]">
                          {o.bill_number}
                        </div>
                        <div className="text-[14px] font-semibold text-slate-800 mt-0.5">
                          {o.customer_name}
                        </div>
                        {o.delivery_area_name && (
                          <div className="text-[11px] text-slate-500 mt-0.5">{o.delivery_area_name}</div>
                        )}
                      </div>
                    <div className="flex flex-col items-end gap-1">
                      <Pill tone={t.tone}>{o.status}</Pill>
                      {o.delivery_settled && (
                        <Pill tone="green">Settled</Pill>
                      )}
                    </div>
                  </div>

                    <div className="mt-2 space-y-1 text-[12.5px] text-slate-600">
                      <div className="flex items-center gap-2">
                        <Phone size={12} className="text-slate-400" /> {o.phone || "—"}
                      </div>
                      <div className="flex items-start gap-2">
                        <MapPin size={12} className="text-slate-400 mt-0.5 shrink-0" />
                        {o.address || "—"}
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock size={12} className="text-slate-400" />
                        ETA: {o.eta_label || (o.eta_minutes ? `${o.eta_minutes} min` : "—")}
                      </div>
                    </div>

                    <div className="mt-3 flex items-end justify-between">
                      <div>
                        <div className="text-[10.5px] uppercase tracking-wide text-slate-400">
                          Amount
                        </div>
                        <div className="text-[16px] font-bold text-slate-900">{aed(o.amount)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10.5px] uppercase tracking-wide text-slate-400">
                          Driver
                        </div>
                        <div className="text-[12.5px] font-medium text-slate-700">
                          {o.driver || <span className="text-slate-400">Unassigned</span>}
                        </div>
                      </div>
                    </div>

                    {isAssigning && (
                      <div className="mt-3 flex gap-2 items-center">
                        <Select value={selectedDriverId} onValueChange={setSelectedDriverId}>
                          <SelectTrigger className="h-8 text-[12px] flex-1">
                            <SelectValue placeholder="Select driver" />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map((d) => (
                              <SelectItem key={d.id} value={String(d.id)}>
                                {d.suggested ? "★ " : ""}
                                {d.name}
                                {d.open_orders > 0 ? ` (${d.open_orders})` : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          className="h-8"
                          disabled={!selectedDriverId || assignMutation.isPending}
                          onClick={() =>
                            assignMutation.mutate({
                              orderId: o.id,
                              deliveryPersonId: Number(selectedDriverId),
                            })
                          }
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8"
                          onClick={() => {
                            setAssignOrderId(null);
                            setSelectedDriverId("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    )}

                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11px]"
                        data-testid={`assign-${o.bill_number}`}
                        disabled={!o.can_assign || assignMutation.isPending}
                        onClick={() => {
                          setAssignOrderId(o.id);
                          setSelectedDriverId(o.delivery_person_id ? String(o.delivery_person_id) : "");
                        }}
                      >
                        <UserPlus size={12} className="mr-1" /> Assign
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11px]"
                        data-testid={`dispatch-${o.bill_number}`}
                        disabled={!o.can_dispatch || statusMutation.isPending}
                        onClick={() => statusMutation.mutate({ orderId: o.id, advance: true })}
                      >
                        <Send size={12} className="mr-1" /> Dispatch
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11px]"
                        data-testid={`complete-${o.bill_number}`}
                        disabled={!o.can_complete || statusMutation.isPending}
                        onClick={() => statusMutation.mutate({ orderId: o.id, advance: true })}
                      >
                        <CheckCircle2 size={12} className="mr-1" /> Complete
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11px]"
                        data-testid={`settle-${o.bill_number}`}
                        disabled={!o.can_settle || settleMutation.isPending}
                        onClick={() => settleMutation.mutate({ orderId: o.id })}
                      >
                        <Banknote size={12} className="mr-1" /> Settle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 text-[11px]"
                        data-testid={`print-${o.bill_number}`}
                        disabled={o.bill_status !== "paid" || printMutation.isPending}
                        onClick={() => printMutation.mutate(o.id)}
                      >
                        <Printer size={12} className="mr-1" /> Print
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Card className="p-0 h-fit">
          <div className="px-4 py-3 border-b">
            <h2 className="text-[14px] font-semibold text-slate-800">Delivery Drivers</h2>
            <div className="text-[11.5px] text-slate-500 mt-0.5">
              {drivers.length} drivers available
            </div>
          </div>
          <ul className="divide-y">
            {drivers.map((d) => (
              <li key={d.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`driver-${d.code}`}>
                <div className="h-8 w-8 rounded-full bg-slate-900 text-white grid place-items-center text-[11px] font-bold">
                  {d.name
                    .split(" ")
                    .map((s) => s[0])
                    .join("")
                    .slice(0, 2)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800">{d.name}</div>
                  <div className="text-[11px] text-slate-500">
                    {d.vehicle || d.code}
                    {d.open_orders > 0 ? ` · ${d.open_orders} active` : ""}
                  </div>
                </div>
                <Pill
                  tone={
                    d.availability === "Available"
                      ? "green"
                      : d.availability === "On Delivery"
                        ? "blue"
                        : "default"
                  }
                >
                  {d.availability}
                </Pill>
              </li>
            ))}
            {drivers.length === 0 && (
              <li className="px-4 py-6 text-center text-[12px] text-slate-500">No drivers on roster</li>
            )}
          </ul>
        </Card>
      </div>
      )}
    </PageContainer>
  );
}
